
import React, { useState, useEffect, useContext } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle } from 'lucide-react';
import { useGooglePlacesAutocomplete, PlaceResult } from '@/hooks/googlePlaces';
import { BusinessDetails } from './types';
import { useToast } from '@/components/ui/use-toast';
// DataContext import removed
import { usStates } from '@/utils/stateOptions';

interface AddressAutocompleteProps {
  businessDetails: BusinessDetails;
  onAddressSelected: (addressData: Partial<BusinessDetails>) => void;
  onManualAddressChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

const AddressAutocomplete: React.FC<AddressAutocompleteProps> = ({
  businessDetails,
  onAddressSelected,
  onManualAddressChange
}) => {
  // API key is now sourced from environment variables at a higher level (App.tsx loads the script)
  // This component will receive the key via props or useGooglePlacesAutocomplete will get it.
  // For now, assume useGooglePlacesAutocomplete handles API key if not passed, or it's passed from parent.
  // Let's simplify: useGooglePlacesAutocomplete now expects apiKey.
  const apiKeyFromEnv = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || "";
  const { toast } = useToast();
  
  const { isLoaded, error, isLoadingScript, inputRef, getPlaceDetails, place } = useGooglePlacesAutocomplete({ 
    apiKey: apiKeyFromEnv 
  });
  
  const [inputValue, setInputValue] = useState(businessDetails.address || '');

  // useEffect for saving API key to localStorage and setAppData is removed.
  // API key is managed by environment variables.

  // Watch for place changes and process them
  useEffect(() => {
    if (place) {
      const placeResult = getPlaceDetails();
      if (placeResult) {
        handleAddressData(placeResult);
      }
    }
  }, [place]);

  // Find the matching state value from our state options by full state name
  const getValidStateValue = (stateName: string): string => {
    if (!stateName) return '';
    
    console.log("Finding state match for:", stateName);
    
    // Find full state name match
    const nameMatch = usStates.find(s => 
      s.label.toLowerCase() === stateName.toLowerCase()
    );
    
    if (nameMatch) {
      console.log("Found state match:", nameMatch.value);
      return nameMatch.value;
    }
    
    // If no match by name, try to match by value (likely a state code)
    const valueMatch = usStates.find(s => 
      s.value.toLowerCase() === stateName.toLowerCase()
    );
    
    if (valueMatch) {
      console.log("Found state value match:", valueMatch.value);
      return valueMatch.value;
    }
    
    // Return the original value if no match found
    console.log("No matching state found for:", stateName);
    return stateName;
  };

  // Process the selected place data
  const handleAddressData = (placeResult: PlaceResult) => {
    console.log("Processing place selection:", placeResult);
    
    // Convert from latitude/longitude to lat/lng for BusinessDetails
    const coordinates = placeResult.coordinates ? {
      latitude: placeResult.coordinates.latitude, // Changed to latitude
      longitude: placeResult.coordinates.longitude // Changed to longitude
    } : undefined;
    
    // Get valid state value from the state name returned by Google Places
    const stateValue = placeResult.state ? getValidStateValue(placeResult.state) : '';
    console.log("Converted state value:", stateValue);
    
    // Create the address data object with the validated state value
    const addressData = {
      address: placeResult.address,
      city: placeResult.city,
      state: stateValue,
      zip: placeResult.zip,
      coordinates
    };
    
    console.log("Sending address data to parent:", addressData);
    onAddressSelected(addressData);

    toast({
      title: "Address selected",
      description: "The address fields have been auto-filled.",
    });
  };

  // Update input value when businessDetails change
  useEffect(() => {
    setInputValue(businessDetails.address || '');
  }, [businessDetails.address]);

  // Handle input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    if (onManualAddressChange) {
      const syntheticEvent = {
        ...e,
        target: {
          ...e.target,
          name: 'address',
          value: e.target.value
        }
      } as React.ChangeEvent<HTMLInputElement>;
      onManualAddressChange(syntheticEvent);
    }
  };

  // UI for manual API key input is removed. Key must be in .env.
  if (!apiKeyFromEnv && !isLoaded && !isLoadingScript) { // Show error if key is missing and not already loading/loaded
    return (
      <div className="space-y-2">
        <label htmlFor="address" className="text-sm font-medium">Street Address</label>
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Google Maps API key (VITE_GOOGLE_MAPS_API_KEY) is not configured. Address autocomplete is disabled.
          </AlertDescription>
        </Alert>
        <Input
          id="address"
          value={inputValue}
          onChange={handleInputChange}
          placeholder="Address autocomplete disabled"
          disabled
        />
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex justify-between items-center">
        <label htmlFor="address" className="text-sm font-medium">Street Address</label>
      </div>
      
      {error && !isLoadingScript && ( // Show error only if not loading script (to avoid showing error during initial load attempt)
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="flex items-center justify-between">
            <span>Error with Google Maps Places API: {error}</span> {/* Changed error.message to error */}
          </AlertDescription>
        </Alert>
      )}
      
      <Input
        id="address"
        ref={inputRef}
        value={inputValue}
        onChange={handleInputChange}
        placeholder="Start typing your address..."
        className={error ? "border-red-500" : ""}
        disabled={!isLoaded || !!error || isLoadingScript}
      />
      
      {isLoadingScript && (
        <p className="text-xs text-gray-500">Loading address search...</p>
      )}
    </div>
  );
};

export default AddressAutocomplete;
